import React, { useMemo, useState } from "react"
import { Container, Row, Col, Input } from "reactstrap"
import { Link } from "react-router-dom"
import { graphql } from "@apollo/client/react/hoc"
import AttributeConfigurations from "./AttributeConfigurations"
import LockedIndicator from "../Material/LockedIndicator"
import { isPermitted, NO_PERMISSION_MESSAGE } from "../Functions/permissions"
import { HasPermissionsQuery } from "../Functions/querys"
import { ROUTES } from "../Layout/routes"
import "./AttributeIndex.css"

// Derived from AttributeConfigurations so adding an attribute still means editing
// only that one config file.
const attributeEntries = (configurations) =>
    Object.keys(configurations)
        .map(key => ({
            key,
            title: configurations[key].title,
            permissionRequired: configurations[key].permissions ? configurations[key].permissions.get : null,
        }))
        .sort((a, b) => a.title.localeCompare(b.title))

const AttributeIndex = ({ configurations = AttributeConfigurations, hasPermissions, loading = false }) => {
    const [searchTerm, setSearchTerm] = useState("")

    const entries = useMemo(() => attributeEntries(configurations), [configurations])
    const visible = useMemo(() => {
        const term = searchTerm.trim().toLowerCase()
        if (!term) return entries
        return entries.filter(entry => entry.title && entry.title.toLowerCase().includes(term))
    }, [entries, searchTerm])

    return (
        <Container fluid className="bos-listing-container bos-attribute-index">
            <Row className="bos-attribute-index__toolbar">
                <Col xs={12} md={4}>
                    <Input
                        type="search"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        placeholder="Search attributes..."
                        aria-label="Search attributes"
                    />
                </Col>
            </Row>
            {visible.length === 0 ?
                <Row><Col><p className="bos-attribute-index__empty">No attributes match "{searchTerm}".</p></Col></Row> :
                <Row className="bos-attribute-index__grid">
                    {visible.map(entry => {
                        const permitted = !loading && isPermitted(entry, hasPermissions)
                        const lockedMessage = `${entry.title} — ${NO_PERMISSION_MESSAGE}`
                        return (
                            <Col xs={12} sm={6} md={4} lg={3} key={entry.key}>
                                <div className={["bos-attribute-index__card", permitted ? "" : "bos-locked"].filter(Boolean).join(" ")}>
                                    <i className="fa fa-database bos-attribute-index__icon" aria-hidden="true" />
                                    {permitted ?
                                        <Link to={`${ROUTES.attributes.path}${entry.key}`}>{entry.title}</Link> :
                                        <>
                                            <span
                                                className="bos-attribute-index__locked-label"
                                                aria-label={lockedMessage}
                                                aria-disabled="true"
                                                tabIndex={0}
                                            >
                                                {entry.title}
                                                <LockedIndicator decorative message={lockedMessage} />
                                            </span>
                                            {/* Sibling of the label, not nested inside it: .bos-locked mutes the
                                                card's contents via opacity (see AttributeIndex.css), and opacity
                                                applies to a whole subtree, so this has to live outside the muted
                                                element to paint at full strength on hover/focus. */}
                                            <span className="bos-attribute-index__locked-reason">{lockedMessage}</span>
                                        </>
                                    }
                                </div>
                            </Col>
                        )
                    })}
                </Row>
            }
        </Container>
    )
}

export { AttributeIndex, attributeEntries }

export default graphql(HasPermissionsQuery, {
    options: () => ({
        variables: { userPermissions: attributeEntries(AttributeConfigurations).map(entry => entry.permissionRequired).filter(Boolean) },
    }),
    props: ({ data: { hasPermissions, loading } }) => ({ hasPermissions, loading }),
})(AttributeIndex)
